import { Upload, DetailedError } from "tus-js-client";
import { isNumber } from "lodash-es";
import axios from "axios";
import pRetry, { AbortError, FailedAttemptError } from "p-retry";
import pLimit from "p-limit";

import { GLOBAL_API_CLIENT } from "@/modules/api/globalApiClient";
import { api } from "@/modules/api";
import { normalizeFilename } from "@/modules/file/normalizeFileName";
import { convertEncodedFileContentToBlob } from "@/modules/file/blob";
import { logger } from "@/modules/logger";
import type {
  UploadImportFileParams,
  ImportFileUploadResult,
  UploadAttachmentsParams,
  UploadAttachmentsResult,
  UploadableAttachment,
  SkippedAttachment,
} from "./types";
import { generateImportTusFingerprint, constructBlobName } from "./utils";

const GCS_UPLOAD_BUCKET_URL = "https://storage.googleapis.com/mem-uploads";
const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

const TUS_ENDPOINT = "https://storage-api-gke.mem.ai/files";
const TUS_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const TUS_RETRY_DELAYS = [0, 1000, 3000, 7000, 15000];

// Concurrency limit for TUS requests (initial uploads + retries).
// This prevents overwhelming the server when many uploads are queued or retrying.
// As of Q1 2026, 5 avoids triggering rate limits. 10 is too high.
const TUS_CONCURRENCY_LIMIT = 5;
const tusRequestLimiter = pLimit(TUS_CONCURRENCY_LIMIT);

// Retry config for 403 errors (exponential backoff via p-retry)
// These can happen when the infra blocks us (usually a rate-limit block)
const RETRY_COUNT = 5;
const RETRY_MIN_TIMEOUT_MS = 2_000;
const RETRY_MAX_TIMEOUT_MS = 30_000;

interface ErrorClassification {
  isRetryable: boolean;
  httpStatus: number | null;
  errorMessage: string | null;
  causingMessage: string | null;
}

/**
 * Classify a TUS error to determine if it's retryable and extract debug info.
 *
 * When the server returns a 403 without CORS headers, the browser treats it
 * as a CORS error and won't expose the response status to JavaScript.
 * We can't read the actual status in that case, so we also check for
 * network/CORS error patterns.
 */
function classifyError(error: Error | DetailedError): ErrorClassification {
  const errorMessage = error.message?.toLowerCase() ?? null;
  const causingMessage =
    "causingError" in error ? (error.causingError?.message?.toLowerCase() ?? null) : null;

  // Check for explicit 403 status when available
  if ("originalResponse" in error && error.originalResponse) {
    const status = error.originalResponse.getStatus?.();
    if (status === 403) {
      return {
        isRetryable: true,
        httpStatus: status,
        errorMessage,
        causingMessage,
      };
    }
  }

  // CORS errors from blocks won't expose the response status.
  // Check for network/CORS error patterns that suggest expected blocking like rate limits.
  const retryablePatterns = [
    "cors",
    "network",
    "failed to fetch",
    "load failed",
    "networkerror",
    "cross-origin",
  ];

  for (const pattern of retryablePatterns) {
    if (errorMessage?.includes(pattern) || causingMessage?.includes(pattern)) {
      return {
        isRetryable: true,
        httpStatus: null,
        errorMessage,
        causingMessage,
      };
    }
  }

  return { isRetryable: false, httpStatus: null, errorMessage, causingMessage };
}

export async function uploadImportFile(
  params: UploadImportFileParams
): Promise<ImportFileUploadResult> {
  const { blob, fileId, filename, mimeType, config, onProgress, signal } = params;

  let normalizedFilename: string;
  try {
    normalizedFilename = normalizeFilename({ fileName: filename });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Invalid filename";
    logger.error({
      message: "[import-tus-service] filename normalization failed",
      info: { fileId, filename, error: errorMessage },
    });
    return { success: false, error: errorMessage };
  }

  const authToken = GLOBAL_API_CLIENT.getAuthToken();
  if (!authToken) {
    logger.error({
      message: "[import-tus-service] no auth token available",
      info: { fileId },
    });
    return { success: false, error: "No auth token available" };
  }

  const blobName = constructBlobName({
    spaceAccountId: config.spaceAccountId,
    fileId,
    filename: normalizedFilename,
  });

  logger.info({
    message: "[import-tus-service] upload start",
    info: {
      fileId,
      filename: normalizedFilename,
      mimeType,
      sizeBytes: blob.size,
      blobName,
    },
  });

  const onFailedAttempt = (error: FailedAttemptError) => {
    logger.warn({
      message: "[import-tus-service] blocked upload, will retry",
      info: {
        fileId,
        filename: normalizedFilename,
        attemptNumber: error.attemptNumber,
        retriesLeft: error.retriesLeft,
      },
    });
  };

  const uploadStartTime = Date.now();

  try {
    await pRetry(
      async () => {
        // Use concurrency limiter to prevent overwhelming the server.
        // This limits both initial uploads AND retries to TUS_CONCURRENCY_LIMIT concurrent requests.
        const result = await tusRequestLimiter(() =>
          attemptTusUpload({
            blob,
            fileId,
            normalizedFilename,
            mimeType,
            blobName,
            authToken,
            config,
            onProgress,
            signal,
            uploadStartTime,
          })
        );

        if (!result.success) {
          // Throw AbortError for non-retryable errors to stop p-retry immediately
          if (!result.retryInfo.isRetryable) {
            throw new AbortError(result.error);
          }
          throw new Error(result.error);
        }
      },
      {
        retries: RETRY_COUNT,
        minTimeout: RETRY_MIN_TIMEOUT_MS,
        maxTimeout: RETRY_MAX_TIMEOUT_MS,
        randomize: true, // Adds jitter (1-2x multiplier) to avoid thundering herd
        onFailedAttempt,
        signal,
      }
    );

    return { success: true, blobName };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Upload failed";
    return { success: false, error: errorMessage };
  }
}

interface AttemptTusUploadParams {
  blob: Blob;
  fileId: string;
  normalizedFilename: string;
  mimeType: string;
  blobName: string;
  authToken: string;
  config: UploadImportFileParams["config"];
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  uploadStartTime: number;
}

type AttemptResult =
  | { success: true; blobName: string }
  | { success: false; error: string; retryInfo: ErrorClassification };

async function attemptTusUpload(params: AttemptTusUploadParams): Promise<AttemptResult> {
  const {
    blob,
    fileId,
    normalizedFilename,
    mimeType,
    blobName,
    authToken,
    config,
    onProgress,
    signal,
    uploadStartTime,
  } = params;

  return new Promise(resolve => {
    const upload = new Upload(blob, {
      endpoint: TUS_ENDPOINT,
      uploadSize: blob.size,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: TUS_RETRY_DELAYS,
      removeFingerprintOnSuccess: true,
      fingerprint: async (file: File, options: InstanceType<typeof Upload>["options"]) => {
        const fileSize = (() => {
          if (!isNumber(file.size)) {
            return "unknown";
          }
          return file.size;
        })();
        const endpoint = (() => {
          if (!options.endpoint) {
            return "unknown";
          }
          return options.endpoint;
        })();

        return generateImportTusFingerprint({
          fileId,
          filename: normalizedFilename,
          fileSize,
          endpoint,
        });
      },
      metadata: {
        filename: normalizedFilename,
        filetype: mimeType,
        space_account_id: config.spaceAccountId,
        file_id: fileId,
      },
      headers: {
        Authorization: `OAuthAccessToken ${authToken}`,
      },
      onProgress: (bytesSent: number, bytesTotal: number) => {
        if (onProgress && bytesTotal > 0) {
          const percent = Math.floor((bytesSent / bytesTotal) * 100);
          onProgress(percent);
        }
      },
      onSuccess: () => {
        logger.info({
          message: "[import-tus-service] upload complete",
          info: {
            fileId,
            filename: normalizedFilename,
            blobName,
            durationMs: Date.now() - uploadStartTime,
          },
        });
        resolve({ success: true, blobName });
      },
      onError: (error: Error | DetailedError) => {
        const { isRetryable, httpStatus, errorMessage, causingMessage } = classifyError(error);

        // Extract additional error details for debugging
        if (!isRetryable) {
          // Only log as error for non-retryable failures (expected blocks, like rate limits, will be logged at warn level in retry loop)
          logger.error({
            message: "[import-tus-service] upload failed",
            info: {
              fileId,
              filename: normalizedFilename,
              error: errorMessage,
              httpStatus,
              causingMessage,
              durationMs: Date.now() - uploadStartTime,
            },
          });
        } else {
          logger.info({
            message: "[import-tus-service] detected retryable error",
            info: {
              fileId,
              filename: normalizedFilename,
              error: errorMessage,
              httpStatus,
              causingMessage,
              durationMs: Date.now() - uploadStartTime,
            },
          });
        }

        resolve({
          success: false,
          error: errorMessage ?? "Upload failed",
          retryInfo: { isRetryable, httpStatus, errorMessage, causingMessage },
        });
      },
    });

    if (signal) {
      if (signal.aborted) {
        resolve({
          success: false,
          error: "Upload aborted",
          retryInfo: {
            isRetryable: false,
            httpStatus: null,
            errorMessage: null,
            causingMessage: null,
          },
        });
        return;
      }
      // TUS calls onError when abort(true) is called, which will resolve the promise
      signal.addEventListener("abort", () => upload.abort(true));
    }

    upload.start();
  });
}

function checkAttachmentUploadability(
  attachment: UploadableAttachment
): { canUpload: true } | { canUpload: false; reason: string; kind: "size" } {
  const { sizeBytes } = attachment;

  if (sizeBytes > MAX_FILE_SIZE_BYTES) {
    return {
      canUpload: false,
      reason: "too large",
      kind: "size",
    };
  }

  return { canUpload: true };
}

export async function uploadAttachments(
  params: UploadAttachmentsParams
): Promise<UploadAttachmentsResult> {
  const { attachments } = params;
  const uploaded = new Map<string, string>();
  const skipped: SkippedAttachment[] = [];

  if (attachments.length === 0) {
    return { uploaded, skipped };
  }

  for (const attachment of attachments) {
    const uploadCheck = checkAttachmentUploadability(attachment);
    if (!uploadCheck.canUpload) {
      logger.warn({
        message: "[uploadAttachments] attachment skipped due to validation",
        info: {
          attachmentId: attachment.id,
          filename: attachment.filename,
          reason: uploadCheck.reason,
        },
      });
      skipped.push({
        id: attachment.id,
        filename: attachment.filename,
        reason: uploadCheck.reason,
        kind: uploadCheck.kind,
      });
      continue;
    }

    try {
      let normalizedFilename: string;
      try {
        normalizedFilename = normalizeFilename({ fileName: attachment.filename });
      } catch {
        logger.warn({
          message: "[uploadAttachments] filename normalization failed, using original",
          info: { attachmentId: attachment.id, filename: attachment.filename },
        });
        normalizedFilename = attachment.filename;
      }

      // Get signed URL from backend
      const { data } = await api.post("/v2/file-uploads/generate-signed-url", {
        body: {
          file_id: attachment.id,
          file_name: normalizedFilename,
          file_mime_type: attachment.mimeType,
        },
      });

      if (!data?.signed_url) {
        logger.warn({
          message: "[uploadAttachments] failed to get signed URL",
          info: { attachmentId: attachment.id, filename: normalizedFilename },
        });
        skipped.push({
          id: attachment.id,
          filename: attachment.filename,
          reason: "upload error",
          kind: "upload_error",
        });
        continue;
      }

      // Convert base64 to blob
      const blob = convertEncodedFileContentToBlob({
        encodedFileContent: attachment.data,
        fileMimeType: attachment.mimeType,
      });

      // Upload to signed URL
      const uploadResult = await axios.put(data.signed_url, blob, {
        headers: {
          "Content-Type": attachment.mimeType,
        },
      });

      if (uploadResult.status < 200 || uploadResult.status >= 300) {
        logger.warn({
          message: "[uploadAttachments] upload failed",
          info: {
            attachmentId: attachment.id,
            filename: normalizedFilename,
            status: uploadResult.status,
          },
        });
        continue;
      }

      // Construct permanent URL: https://storage.googleapis.com/mem-uploads/{fileId}/{filename}
      const permanentUrl = `${GCS_UPLOAD_BUCKET_URL}/${attachment.id}/${normalizedFilename}`;
      uploaded.set(attachment.id, permanentUrl);

      logger.info({
        message: "[uploadAttachments] attachment uploaded successfully",
        info: { attachmentId: attachment.id, filename: normalizedFilename },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error({
        message: "[uploadAttachments] attachment upload error",
        info: {
          attachmentId: attachment.id,
          filename: attachment.filename,
          error: errorMessage,
        },
      });
      skipped.push({
        id: attachment.id,
        filename: attachment.filename,
        reason: `upload error`,
        kind: "upload_error",
      });
    }
  }

  return { uploaded, skipped };
}
